import { createApi } from '@reduxjs/toolkit/query/react'
import { ENDPOINTS } from '@constants/index'
import { CtaConfiguration } from '@typesApp/Cta'
import { PageFlowConfiguration } from '@typesApp/PageFlow'
import { Form, FormContainerComponent } from '@typesApp/Forms'
import { MessageAnimationConfiguration } from '@typesApp/MessageAnimation'
import { transformCtaState } from '@utils/configurationTransform'
import { dbBaseQueryWithReauth } from './baseQuery'

import ctaLoggedOut from '../../__data/base/cta-logged-out.json'
import ctaLoggedOutVD from '../../__data/vde/cta-logged-out.json'

const CONFIGURATIONS = {
  CTA_STATE: '/cta/state',
  CART_BUNDLE: '/cart-product-bundle-mapping',
  PAGE_FLOW: '/insurance-flow/page-flow',
  INTRO_FORM: '/insurance-flow/intro-page',
  INSURERS_FORM: '/insurance-flow/eligible-insurers-page',
  MESSAGE_ANIMATION: '/insurance-flow/message-animation',
} as const

const BASE_URL = `${ENDPOINTS.DIRECT_BILLING.CONFIGURATIONS}?path=frontend-bundle`

export const configApi = createApi({
  reducerPath: 'config',
  baseQuery: dbBaseQueryWithReauth,
  keepUnusedDataFor: Number.MAX_VALUE,
  tagTypes: [
    'CtaInitialConfiguration',
    'CtaConfiguration',
    'IntroPageConfiguration',
    'PageFlowConfiguration',
    'EligibleInsurersPageConfiguration',
    'MessageAnimationConfiguration',
  ],
  endpoints: builder => ({
    getCtaInitialConfiguration: builder.query<CtaConfiguration, string>({
      queryFn: async brand => {
        return { data: getInitialCtaConfiguration(brand) }
      },
      providesTags: [{ type: 'CtaInitialConfiguration' }],
    }),
    getCtaConfiguration: builder.query<CtaConfiguration, void>({
      query: () => ({
        url: `${BASE_URL}${CONFIGURATIONS.CTA_STATE}`,
        method: 'GET',
      }),
      transformResponse: (response: Record<string, Record<string, object>>): CtaConfiguration => {
        const result = transformCtaState(response.state)
        return result
      },
      providesTags: [{ type: 'CtaConfiguration' }],
    }),
    getIntroPageConfiguration: builder.query<Form, void>({
      query: () => ({
        url: `${BASE_URL}${CONFIGURATIONS.INTRO_FORM}`,
        method: 'GET',
      }),
      transformResponse: (response: { introPage: Form }): Form => {
        return response.introPage
      },
      providesTags: [{ type: 'IntroPageConfiguration' }],
    }),
    getEligibleInsurersPageConfiguration: builder.query<Form, void>({
      query: () => ({
        url: `${BASE_URL}${CONFIGURATIONS.INSURERS_FORM}`,
        method: 'GET',
      }),
      transformResponse: (response: { eligibleInsurersPage: Form }): Form => {
        return response?.eligibleInsurersPage
      },
      providesTags: [{ type: 'EligibleInsurersPageConfiguration' }],
    }),
    getPageFlowConfiguration: builder.query<PageFlowConfiguration, void>({
      query: () => ({
        url: `${BASE_URL}${CONFIGURATIONS.PAGE_FLOW}`,
        method: 'GET',
      }),
      transformResponse: (response: Record<string, Record<string, object>>): PageFlowConfiguration => {
        return response.pageFlow as unknown as PageFlowConfiguration
      },
      providesTags: [{ type: 'PageFlowConfiguration' }],
    }),
    getMessageAnimationConfiguration: builder.query<MessageAnimationConfiguration, void>({
      query: () => ({
        url: `${BASE_URL}${CONFIGURATIONS.MESSAGE_ANIMATION}`,
        method: 'GET',
      }),
      transformResponse: (response: Record<string, Record<string, object>>): MessageAnimationConfiguration => {
        return response.messageAnimation as unknown as MessageAnimationConfiguration
      },
      providesTags: [{ type: 'MessageAnimationConfiguration' }],
    }),
  }),
})

export const {
  useGetCtaInitialConfigurationQuery,
  useGetCtaConfigurationQuery,
  useGetIntroPageConfigurationQuery,
  useGetPageFlowConfigurationQuery,
  useGetEligibleInsurersPageConfigurationQuery,
  useGetMessageAnimationConfigurationQuery,
} = configApi

/**
 * Need to use the static json for loggedOut state as we cannot
 * fetch from configurations until customer is logged in
 */
const CTA_LOGGEDOUT_COMPONENT = {
  base: ctaLoggedOut,
  vde: ctaLoggedOutVD,
} as Record<string, FormContainerComponent>

const getInitialCtaConfiguration = (brand: string): CtaConfiguration => {
  return {
    loggedOut: CTA_LOGGEDOUT_COMPONENT[brand] || CTA_LOGGEDOUT_COMPONENT.base,
  } as CtaConfiguration
}
